/* Native GET forms and links are the baseline. This element owns enhancement only. */
(() => {
  if (customElements.get('pbn-collection')) return;

  class PbnCollection extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.sequence ??= 0;
      this.pressing = false;
      this.mobile = matchMedia('(width < 750px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.panelRevision = 0;
      this.feedback = new Map();
      const options = { signal: this.events.signal };
      this.installCollectionImageWarmup(options);
      this.installFocusResume(options);
      this.installSelectorFill();
      this.addEventListener('click', (event) => this.pressFeedback(event), options);
      this.addEventListener('pointerdown', (event) => {
        this.pressing = true;
        if (event.button === 0 && event.isPrimary !== false) this.selectorFillOrigin(event);
      }, options);
      const release = () => { this.pressing = false; this.releasePress?.(); };
      document.addEventListener('pointerup', release, options);
      document.addEventListener('pointercancel', release, options);
      window.addEventListener('blur', release, options);
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('change', (event) => this.onChange(event), options);
      this.addEventListener('submit', (event) => this.onSubmit(event), options);
      this.addEventListener('keydown', (event) => {
        if ([' ', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) this.selectorFillOrigin(event);
      }, options);
      document.addEventListener('keydown', (event) => {
        // Removing a focused chip can move focus to body before Escape bubbles.
        // Keep the anchored panel dismissible without intercepting another UI.
        const ownedTarget = this.contains(event.target) || event.target === document.body || event.target === document.documentElement;
        if (event.key === 'Escape' && !event.defaultPrevented && this.panelOwner && !this.dialog && ownedTarget) {
          this.nextPanel = null;
          this.closePanels(true);
        }
      }, options);
      document.addEventListener('click', (event) => {
        // Removed chips retain their original event path after optimistic removal.
        if (!event.composedPath().includes(this) && !this.dialog) { this.nextPanel = null; this.closePanels(false); }
      }, options);
      window.addEventListener('popstate', () => {
        const url = new URL(location.href);
        if (url.pathname === new URL(this.dataset.collectionUrl, location.origin).pathname) {
          this.closePanels(false, { immediate: true });
          this.navigate(url, { history: false });
        }
      }, options);
      this.mobile.addEventListener('change', () => this.closePanels(true, { immediate: true }), options);
      this.reduced.addEventListener('change', () => {
        if (this.closing) this.closePanels(true, { immediate: true });
        else this.panelMotion?.cancel();
        this.cancelChipMotion();
        this.cancelFeedback();
      }, options);
      // Theme Editor block selection opens its containing panel, using the same lifecycle.
      this.addEventListener('shopify:block:select', (event) => {
        const details = event.target.closest('details');
        if (details) this.openPanel(details);
      }, options);
      this.setAttribute('data-enhanced', '');
      this.updateSelections();
    }

    disconnectedCallback() {
      this.sequence += 1;
      this.request?.abort();
      this.events?.abort();
      this.pendingUrl = null;
      this.removeAttribute('aria-busy');
      const status = this.querySelector('[data-pbn-status]');
      if (status) status.textContent = '';
      this.closePanels(false, { immediate: true });
      this.cancelFeedback();
      this.querySelectorAll('.pbn-collection__selector-fill').forEach((fill) => fill.remove());
      this.cancelChipMotion();
      this.events = null;
      this.removeAttribute('data-enhanced');
    }

    installCollectionImageWarmup(options) {
      const artwork = this.querySelector('.pbn-collection__accessories img');
      if (artwork) {
        const updateError = () => { artwork.style.visibility = artwork.complete && !artwork.naturalWidth ? 'hidden' : ''; };
        artwork.addEventListener('error', updateError, options);
        artwork.addEventListener('load', updateError, options);
        updateError();
      }
      const intent = (event) => {
        if (event.target.closest?.('summary[id^="PbnCategoryTrigger-"]')) this.warmCollectionImages();
      };
      this.addEventListener('pointerover', intent, options);
      this.addEventListener('pointerdown', intent, options);
      this.addEventListener('focusin', intent, options);
    }

    warmCollectionImages(open = false) {
      const connection = navigator.connection;
      // Missing network information is not permission for speculative traffic.
      if (!open && (!connection || connection.saveData || /2g/.test(connection.effectiveType || ''))) return;
      const candidates = [...this.querySelectorAll('.pbn-collection__categories img')];
      const images = open ? candidates.filter(image => {
        const rect = image.getBoundingClientRect();
        const scrollport = image.closest('.pbn-collection__panel-body');
        const body = scrollport?.getBoundingClientRect();
        // The opening transform can still place the whole dialog offscreen.
        // Its own scrollport already defines which images will appear first.
        const top = body?.top || 0, bottom = body ? top + Math.min(scrollport.clientHeight, innerHeight) : innerHeight;
        return rect.width > 0 && rect.height > 0 && rect.top < bottom && rect.bottom > top;
      }) : candidates.slice(0, 2);
      for (const image of images) {
        if (image.complete && image.naturalWidth > 0) continue;
        image.fetchPriority = open ? 'auto' : 'low';
        image.loading = 'eager';
        image.decode?.().catch(() => {});
      }
    }

    formUrl(form) {
      const url = new URL(form.action, location.origin);
      const params = new URLSearchParams(new FormData(form));
      params.delete('page');
      // Preview identity belongs to the current browser session, never theme source.
      const current = new URL(location.href);
      for (const name of ['preview_theme_id', 'view']) {
        const value = current.searchParams.get(name);
        if (value) params.set(name, value);
      }
      url.search = params.toString();
      return url;
    }

    onChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement) || !input.form) return;
      if (this.draft && input.closest('[data-pbn-panel]')) {
        this.updateSelections({ animate: true });
        return;
      }
      if (this.draft) this.closePanels(false);
      if (input.name === 'sort_by') {
        const other = this.querySelector('.pbn-collection__other-sort select');
        if (other && input !== other) other.disabled = true;
        if (input === other) this.querySelectorAll('input[name="sort_by"]').forEach((radio) => { radio.checked = false; });
      }
      this.updateSelections();
      this.navigate(this.formUrl(input.form));
    }

    onSubmit(event) {
      if (!(event.target instanceof HTMLFormElement)) return;
      event.preventDefault();
      const url = this.formUrl(event.target);
      this.draft = null;
      this.updateSelections();
      this.closePanels(true);
      // A return to the visible URL must still cancel a different pending Apply.
      if (url.href === this.pendingUrl || (!this.pendingUrl && url.href === location.href)) return;
      this.navigate(url);
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const close = event.target.closest('[data-pbn-close]');
      if (close) {
        event.preventDefault();
        this.nextPanel = null;
        this.closePanels(true);
        return;
      }
      const summary = event.target.closest('summary');
      if (summary) {
        event.preventDefault();
        const details = summary.parentElement;
        if ((this.panelOwner === details || details.open) && !this.closing) this.closePanels(true);
        else this.openPanel(details);
        return;
      }
      const link = event.target.closest('a[data-pbn-navigation]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target || link.hasAttribute('download')) return;
      let url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname !== new URL(this.dataset.collectionUrl, location.origin).pathname) return;
      event.preventDefault();
      if (link.hasAttribute('data-pbn-remove')) {
        this.querySelectorAll('input[type="checkbox"]').forEach((input) => {
          if (input.name === link.dataset.param && input.value === link.dataset.value) input.checked = false;
        });
        url = this.formUrl(this.querySelector('form'));
        this.updateSelections({ animate: true });
      } else if (link.hasAttribute('data-pbn-reset')) {
        // Reset is an immediate navigation, including when the category panel
        // has pending selections. Dismissal must not restore that draft.
        this.draft = null;
        this.querySelectorAll('input[type="checkbox"]').forEach((input) => { input.checked = false; });
        this.querySelectorAll('input[name="sort_by"]').forEach((input) => { input.checked = input.value === this.dataset.defaultSort; });
        this.querySelectorAll('input[type="hidden"]').forEach((input) => input.remove());
        const other = this.querySelector('.pbn-collection__other-sort select');
        if (other) other.disabled = true;
        this.closePanels(false);
        url = this.formUrl(this.querySelector('form'));
        this.updateSelections();
      }
      const page = link.hasAttribute('data-pbn-page');
      if (this.draft && link.hasAttribute('data-pbn-remove')) return;
      if (page) this.closePanels(false);
      this.navigate(url, { page });
    }

    openPanel(details) {
      if (this.panelOwner && this.panelOwner !== details) {
        this.nextPanel = details;
        this.closePanels(false);
        return;
      }
      this.nextPanel = null;
      const panel = this.dialog?.querySelector('.pbn-collection__panel') || details.querySelector('.pbn-collection__panel');
      if (!panel) return;
      if (details.classList.contains('pbn-collection__disclosure--categories')) requestAnimationFrame(() => {
        if (!this.events?.signal.aborted && this.panelOwner === details) this.warmCollectionImages(true);
      });
      const continuing = this.panelOwner === details;
      const surface = this.dialog || panel;
      const from = continuing ? getComputedStyle(surface).transform : null;
      this.panelRevision += 1;
      this.panelMotion?.cancel();
      this.closing = false;
      panel.inert = false;
      this.dialog?.removeAttribute('data-closing');
      this.panelOwner = details;
      const controls = [...panel.querySelectorAll('input[type="checkbox"]')];
      this.draft = {
        panel,
        values: new Map(controls.map((input) => [input.id, input.checked])),
        availability: new Map(controls.map((input) => [input.id, input.disabled]))
      };
      this.updateSelections();
      if (!this.mobile.matches || typeof HTMLDialogElement === 'undefined') {
        details.open = true;
        this.animatePanel(panel, true, from);
        return;
      }
      if (this.dialog) {
        this.animatePanel(this.dialog, true, from);
        return;
      }
      const dialog = document.createElement('dialog');
      dialog.className = 'pbn-collection__dialog';
      dialog.dataset.kind = details.dataset.panelKind;
      dialog.setAttribute('aria-labelledby', panel.getAttribute('aria-labelledby'));
      this.dialog = dialog;
      const chips = panel.querySelector('[data-pbn-chip-wrap]');
      if (chips) panel.querySelector('.pbn-collection__panel-body').prepend(chips);
      details.querySelector('summary').setAttribute('aria-expanded', 'true');
      dialog.append(panel);
      this.append(dialog);
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closePanels(true);
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) this.closePanels(true);
      });
      // Buttons and checkboxes retain their original GET form after moving into a dialog.
      panel.querySelectorAll('button[type="submit"]').forEach((button) => { button.setAttribute('form', this.querySelector('form').id); });
      // Preserve fractional layout width while the native modal locks page scroll.
      const bodyWidth = document.body.getBoundingClientRect().width;
      const bodyPadding = parseFloat(getComputedStyle(document.body).paddingRight);
      dialog.showModal();
      const scrollbar = Math.max(0, document.body.getBoundingClientRect().width - bodyWidth);
      document.documentElement.style.setProperty('--pbn-page-padding', `${bodyPadding + scrollbar}px`);
      this.animatePanel(dialog, true);
    }

    animatePanel(surface, entering, from) {
      if (this.reduced?.matches || !surface.animate) return null;
      const offset = this.mobile.matches ? 'translateY(100%)' : 'translateY(12px)';
      const frames = entering
        ? [{ transform: from && from !== 'none' ? from : offset, opacity: this.mobile.matches ? 1 : 0 }, { transform: 'translateY(0)', opacity: 1 }]
        : [{ transform: from || 'translateY(0)', opacity: 1 }, { transform: offset, opacity: this.mobile.matches ? 1 : 0 }];
      const animation = surface.animate(frames, { duration: entering ? 300 : 260, easing: entering ? 'cubic-bezier(0.2, 0, 0, 1)' : 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'both' });
      this.panelMotion = animation;
      if (entering) animation.onfinish = () => { if (this.panelMotion === animation) { animation.cancel(); this.panelMotion = null; } };
      return animation;
    }

    closePanels(returnFocus, { immediate = false } = {}) {
      if (immediate) this.nextPanel = null;
      const details = this.panelOwner || this.querySelector('details[open]');
      const summary = details?.querySelector('summary');
      if (!details) return;
      if (this.closing && !immediate) return;
      const panel = this.dialog?.querySelector('.pbn-collection__panel') || details.querySelector('.pbn-collection__panel');
      // Dismissal ends input ownership immediately, not when its visual exit ends.
      // Otherwise a late Space/tap can submit a choice after draft rollback.
      if (panel) panel.inert = true;
      // Inert focus adjustment may wait for rendering; release it synchronously.
      if (panel?.contains(document.activeElement)) document.activeElement.blur();
      if (this.draft) {
        this.draft.panel.querySelectorAll('input[type="checkbox"]').forEach((input) => {
          input.checked = this.draft.values.get(input.id) || false;
          input.disabled = this.draft.availability?.get(input.id) ?? input.disabled;
        });
        this.draft = null;
        this.updateSelections();
      }
      const surface = this.dialog || details.querySelector('.pbn-collection__panel');
      const from = surface ? getComputedStyle(surface).transform : null;
      const revision = ++this.panelRevision;
      this.panelMotion?.cancel();
      this.closing = true;
      this.dialog?.setAttribute('data-closing', '');
      const finish = () => {
        if (revision !== this.panelRevision) return;
        this.panelMotion?.cancel();
        this.panelMotion = null;
        this.closing = false;
      if (this.dialog) {
        const dialog = this.dialog;
        const panel = dialog.querySelector('.pbn-collection__panel');
        dialog.close();
        const chips = panel?.querySelector('[data-pbn-chip-wrap]');
        if (chips) panel.querySelector('.pbn-collection__panel-top').prepend(chips);
        if (panel && this.panelOwner && this.contains(this.panelOwner)) this.panelOwner.append(panel);
        dialog.remove();
        this.dialog = null;
      }
      this.panelOwner = null;
      if (panel) panel.inert = false;
      document.documentElement.style.removeProperty('--pbn-page-padding');
      this.querySelectorAll('details').forEach((item) => {
        item.open = false;
        item.querySelector('summary')?.removeAttribute('aria-expanded');
      });
        if (returnFocus && summary?.isConnected) {
          summary.focus({ preventScroll: true });
          if (this.focusMode === 'pointer') {
            this.resumeTarget = summary;
            summary.setAttribute('data-pbn-resume-focus', '');
          }
        }
      const next = this.nextPanel;
      this.nextPanel = null;
      if (next?.isConnected) this.openPanel(next);
      };
      const animation = !immediate && surface ? this.animatePanel(surface, false, from) : null;
      if (animation) animation.onfinish = finish;
      else finish();
    }

    // Keep native labels, controls and the top-layer dialog alive during requests.
    updateSelections({ animate = false } = {}) {
      // Draft choices stay local; availability follows the latest applied response.
      // A selected draft value must remain enabled so it can be removed/submitted.
      if (this.draft?.availability) {
        this.draft.panel.querySelectorAll('input[type="checkbox"]').forEach((input) => {
          input.disabled = !!this.draft.availability.get(input.id) && !input.checked;
        });
      }
      const count = this.querySelector('[data-pbn-view-count]');
      if (count) count.hidden = !!this.draft;
      for (const kind of ['categories', 'difficulty']) {
        const inputs = [...this.querySelectorAll(`[data-pbn-panel="${kind}"] input:checked`)];
        const badge = this.querySelector(`[data-pbn-selected-count="${kind}"]`);
        if (badge && !this.draft) {
          badge.textContent = inputs.length;
          badge.hidden = inputs.length === 0;
        }
        if (kind !== 'categories') continue;
        const list = this.querySelector('[data-pbn-active-categories]');
        const template = this.querySelector('[data-pbn-category-chip]');
        if (!list || !template) continue;
        const wrap = list.parentElement;
        const body = this.dialog?.querySelector('.pbn-collection__panel-body');
        const grid = list.closest('[data-pbn-panel]').querySelector('.pbn-collection__categories');
        const wrapRect = wrap.getBoundingClientRect();
        const oldHeight = wrapRect.height;
        const below = body && body.scrollTop > 0 && wrapRect.bottom <= body.getBoundingClientRect().top;
        const oldScroll = body?.scrollTop || 0;
        // Capture the currently painted positions before canceling an interrupted move.
        const oldGrid = grid?.getBoundingClientRect();
        const previous = new Map([...list.children].map((item) => [item, item.getBoundingClientRect()]));
        this.cancelChipMotion();
        const moving = animate && !below && !this.reduced?.matches && !!wrap.animate;
        list.querySelectorAll('a').forEach((link) => {
          if (!inputs.some((input) => input.name === link.dataset.param && input.value === link.dataset.value)) {
            const item = link.parentElement;
            if (moving && item.animate) {
              const rect = previous.get(item), parent = wrapRect;
              const ghost = document.createElement('span');
              ghost.className = 'pbn-collection__chip-ghost';
              ghost.setAttribute('aria-hidden', 'true');
              ghost.textContent = link.querySelector('span').textContent;
              Object.assign(ghost.style, { left: `${rect.left-parent.left}px`, top: `${rect.top-parent.top}px`, width: `${rect.width}px`, height: `${rect.height}px` });
              wrap.append(ghost);
              this.trackChipMotion(
                ghost.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-6px)' }], { duration: 180, easing: 'ease-out' }),
                () => ghost.remove()
              );
            }
            item.remove();
          }
        });
        const formUrl = this.formUrl(this.querySelector('form'));
        for (const input of inputs) {
          let link = [...list.querySelectorAll('a')].find((item) => item.dataset.param === input.name && item.dataset.value === input.value);
          if (!link) {
            const item = template.content.firstElementChild.cloneNode(true);
            link = item.querySelector('a');
            link.dataset.param = input.name;
            link.dataset.value = input.value;
            link.querySelector('span').textContent = input.closest('label').querySelector('.pbn-collection__value-label').textContent;
            link.setAttribute('aria-label', `${template.dataset.removeLabel}: ${link.textContent.trim()}`);
            list.append(item);
          }
          const url = new URL(formUrl.href);
          url.searchParams.delete(input.name, input.value);
          link.href = url.href;
        }
        list.hidden = inputs.length === 0;
        const nextHeight = wrap.getBoundingClientRect().height;
        if (below) body.scrollTop = oldScroll + nextHeight - oldHeight;
        else if (moving) {
          // Commit intrinsic layout once. Only transform/opacity run between frames;
          // animating wrapper height would relayout the entire illustrated grid.
          const nextGrid = grid?.getBoundingClientRect();
          const positions = [...list.children].map((item) => [item, item.getBoundingClientRect()]);
          const timing = { duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)' };
          if (grid && oldGrid.top !== nextGrid.top) {
            this.trackChipMotion(grid.animate([
              { transform: `translateY(${oldGrid.top - nextGrid.top}px)` },
              { transform: 'translateY(0)' }
            ], timing));
          }
          for (const [item, next] of positions) {
            const old = previous.get(item);
            if (old && old.left === next.left && old.top === next.top) continue;
            this.trackChipMotion(item.animate([{ opacity: old ? 1 : 0, transform: old ? `translate(${old.left - next.left}px, ${old.top - next.top}px)` : 'translateY(-6px)' }, { opacity: 1, transform: 'translate(0, 0)' }], timing));
          }
        }
      }
    }

    trackChipMotion(animation, cleanup = () => {}) {
      this.chipMotions ??= new Set();
      this.chipMotions.add(animation);
      return this.trackFeedback(animation, () => {
        this.chipMotions.delete(animation);
        cleanup();
      });
    }

    cancelChipMotion() {
      for (const animation of this.chipMotions || []) this.finishFeedback(animation, { cancelEffect: true });
    }

    trackFeedback(animation, cleanup = () => {}) {
      this.feedback.set(animation, cleanup);
      animation.onfinish = () => this.finishFeedback(animation, { cancelEffect: true });
      animation.oncancel = () => this.finishFeedback(animation);
      return animation;
    }

    finishFeedback(animation, { cancelEffect = false } = {}) {
      const cleanup = this.feedback.get(animation);
      if (!cleanup) return;
      this.feedback.delete(animation);
      try {
        cleanup();
      } finally {
        // Remove transient DOM before cancel exposes the unanimated base style.
        if (cancelEffect) animation.cancel();
      }
    }

    cancelFeedback() {
      for (const animation of [...(this.feedback?.keys() || [])]) {
        this.finishFeedback(animation, { cancelEffect: true });
      }
    }

    installSelectorFill() {
      this.querySelectorAll('.pbn-collection__pill--sort').forEach((control) => {
        if (control.querySelector('.pbn-collection__selector-fill')) return;
        const fill = document.createElement('span');
        fill.className = 'pbn-collection__selector-fill';
        fill.setAttribute('aria-hidden', 'true');
        control.append(fill);
      });
    }

    selectorFillOrigin(event) {
      const control = event.target.closest?.('.pbn-collection__pill--sort');
      const input = control?.querySelector('input');
      if (!input || input.disabled || input.checked || this.reduced?.matches) return;
      const fill = control.querySelector('.pbn-collection__selector-fill');
      if (!fill) return;
      let x = 50, y = 50;
      if (event.type === 'pointerdown') {
        const rect = control.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        x = Math.max(0, Math.min(100, (event.clientX - rect.left) / rect.width * 100));
        y = Math.max(0, Math.min(100, (event.clientY - rect.top) / rect.height * 100));
      }
      fill.style.setProperty('--pbn-fill-x', `${x}%`);
      fill.style.setProperty('--pbn-fill-y', `${y}%`);
      fill.style.setProperty('--pbn-fill-size', `${2 * Math.hypot(Math.max(x, 100 - x), Math.max(y, 100 - y)) + 1}%`);
    }

    pressFeedback(event) {
      const control = event.target.closest?.('button, summary, .pbn-collection__pill, .pbn-collection__value, [data-pbn-remove], [data-pbn-reset]');
      if (control?.matches('.pbn-collection__pill--sort, .pbn-collection__pill--disclosure')) return;
      if (!control || control.matches(':disabled') || this.reduced?.matches || !control.animate || (event.target.matches('input') && event.detail !== 0)) return;
      const rect = control.getBoundingClientRect(), diameter = Math.hypot(rect.width, rect.height) * 2;
      const ripple = document.createElement('span');
      const clip = document.createElement('span');
      clip.className = 'pbn-collection__press-surface';
      clip.setAttribute('aria-hidden', 'true');
      ripple.className = 'pbn-collection__ripple';
      ripple.setAttribute('aria-hidden', 'true');
      const x = event.detail ? event.clientX - rect.left : rect.width / 2;
      const y = event.detail ? event.clientY - rect.top : rect.height / 2;
      Object.assign(ripple.style, { width: `${diameter}px`, height: `${diameter}px`, left: `${x}px`, top: `${y}px` });
      clip.append(ripple);
      control.append(clip);
      this.trackFeedback(
        ripple.animate([{ transform: 'translate(-50%, -50%) scale(0)', opacity: .18 }, { transform: 'translate(-50%, -50%) scale(1)', opacity: 0 }], { duration: 420, easing: 'ease-out' }),
        () => clip.remove()
      );
    }

    reconcile(next) {
      const form = this.querySelector('form');
      const incomingForm = next.querySelector('form');
      const controls = [...this.querySelectorAll('input[id]')];
      for (const input of controls) {
        const incoming = next.querySelector(`#${CSS.escape(input.id)}`);
        if (!incoming) continue;
        if (this.draft?.panel.contains(input)) {
          this.draft.values.set(input.id, incoming.checked);
          this.draft.availability ??= new Map();
          this.draft.availability.set(input.id, incoming.disabled);
        } else {
          input.checked = incoming.checked;
          input.disabled = incoming.disabled;
        }
        const count = input.closest('label')?.querySelector('.pbn-collection__value-count');
        const incomingCount = incoming.closest('label')?.querySelector('.pbn-collection__value-count');
        if (count && incomingCount) count.textContent = incomingCount.textContent;
      }
      form.querySelectorAll('input[type="hidden"]').forEach((input) => input.remove());
      incomingForm.querySelectorAll('input[type="hidden"]').forEach((input) => form.append(input));
      const other = this.querySelector('.pbn-collection__other-sort');
      const incomingOther = next.querySelector('.pbn-collection__other-sort');
      if (other && incomingOther) {
        other.querySelector('select').value = incomingOther.querySelector('select').value;
        other.querySelector('select').disabled = false;
      }
      else if (other) other.remove();
      else if (incomingOther) this.querySelector('.pbn-collection__controls').prepend(incomingOther);
      for (const selector of ['.pbn-collection__count', '[data-pbn-view-count]']) {
        const current = this.querySelector(selector);
        const incoming = next.querySelector(selector);
        if (current && incoming) current.replaceChildren(...incoming.childNodes);
      }
      this.querySelector('.pbn-collection__results').replaceChildren(...next.querySelector('.pbn-collection__results').childNodes);
      Object.assign(this.querySelector('[data-pbn-content]').dataset, next.dataset);
      this.updateSelections();
    }

    installFocusResume(options) {
      const clear = () => {
        this.querySelectorAll('[data-pbn-resume-focus]').forEach((node) => node.removeAttribute('data-pbn-resume-focus'));
      };
      const eligible = (node) => node instanceof Element && this.contains(node) && node.matches('button, a, summary, input[type="checkbox"], input[type="radio"]');
      const restore = () => {
        const node = document.activeElement;
        if (this.focusMode === 'pointer' && node === this.resumeTarget && eligible(node)) node.setAttribute('data-pbn-resume-focus', '');
      };
      document.addEventListener('pointerdown', () => { clear(); this.focusMode = 'pointer'; }, { ...options, capture: true });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.focusMode = 'pointer';
        else if (['Tab', 'Enter', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          this.focusMode = 'keyboard';
          clear();
        }
      }, { ...options, capture: true });
      document.addEventListener('click', (event) => {
        if (event.isTrusted && event.detail === 0 && this.focusMode !== 'keyboard' && !event.pointerType) {
          // Do not infer pointer modality for assistive-technology activation.
          this.focusMode = null;
          clear();
        }
      }, options);
      this.addEventListener('focusout', clear, options);
      this.addEventListener('focusin', restore, options);
      window.addEventListener('blur', () => { this.resumeTarget = document.activeElement; }, options);
      window.addEventListener('focus', restore, options);
      window.addEventListener('pageshow', restore, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.resumeTarget = document.activeElement;
        else restore();
      }, options);
    }

    async settlePress(signal) {
      signal.throwIfAborted();
      if (!this.pressing) return;
      await new Promise((resolve, reject) => {
        const aborted = () => { this.releasePress = null; reject(new DOMException('Aborted', 'AbortError')); };
        this.releasePress = () => {
          signal.removeEventListener('abort', aborted);
          this.releasePress = null;
          requestAnimationFrame(resolve);
        };
        signal.addEventListener('abort', aborted, { once: true });
      });
    }

    async navigate(target, { history: commitHistory = true, page = false } = {}) {
      target = new URL(target);
      const current = new URL(location.href);
      for (const name of ['preview_theme_id', 'view']) {
        if (!target.searchParams.has(name) && current.searchParams.has(name)) target.searchParams.set(name, current.searchParams.get(name));
      }
      this.request?.abort();
      const sequence = ++this.sequence;
      const controller = new AbortController();
      this.request = controller;
      this.pendingUrl = target.href;
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-pbn-status]').textContent = this.dataset.loadingText;
      const requestUrl = new URL(target);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
      try {
        const response = await fetch(requestUrl, { signal: controller.signal, headers: { Accept: 'text/html' }, credentials: 'same-origin' });
        if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Collection response unavailable');
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextRoot = documentFragment.getElementById(this.id);
        const next = nextRoot?.querySelector('[data-pbn-content]');
        if (!next || nextRoot.dataset.sectionId !== this.dataset.sectionId || nextRoot.dataset.collectionUrl !== this.dataset.collectionUrl || !next.querySelector('.pbn-collection__results')) throw new Error('Collection section missing');
        await this.settlePress(controller.signal);
        if (sequence !== this.sequence || !this.isConnected) return;

        const active = document.activeElement;
        const ownedFocus = this.contains(active);
        this.reconcile(next);
        if (commitHistory && target.href !== location.href) history.pushState({ ...history.state, pbnCollection: this.dataset.sectionId }, '', target);
        const canonical = new URL(this.dataset.collectionUrl, location.origin);
        if (Number(next.dataset.page) > 1) canonical.searchParams.set('page', next.dataset.page);
        const canonicalLink = document.querySelector('link[rel="canonical"]');
        if (canonicalLink) canonicalLink.href = canonical.href;
        document.title = next.dataset.pageTitle;
        this.querySelector('[data-pbn-status]').textContent = next.dataset.resultText;
        if (page) {
          const toolbar = this.querySelector('.pbn-collection__toolbar');
          if (ownedFocus) toolbar.focus({ preventScroll: true });
          toolbar.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      } catch (error) {
        if ((timedOut || error.name !== 'AbortError') && sequence === this.sequence && this.isConnected) location.assign(target.href);
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence && this.isConnected) {
          this.removeAttribute('aria-busy');
          this.request = null;
          this.pendingUrl = null;
        }
      }
    }
  }
  customElements.define('pbn-collection', PbnCollection);
})();
